import { useEventContext, useQuizContext } from 'contexts';
import { CSSProperties, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StartScreen } from './start-screen';
import { QuestionSlides } from './question-slides';

import './slideshow-quiz.css';
import { useQuery } from '@apollo/client';
import { getQuizQuestions } from 'graphql/queries';
import { QuizQuestions, QuizQuestionsVariables } from 'graphql/queries/types/QuizQuestions';
import { QuizEndScreen } from './quiz-end-sceen';
import { AnswerSlides } from './answer-slides';
import { NavUtils } from 'utils';
import omit from 'lodash/omit';
import { Question } from 'types';
import { SinglePlayerResultScreen } from './single-player-result-screen';
import { HoverMenu } from 'components';

enum Scenes {
    Title,
    PresentationQuiz,
    QuizEndScreen,
    AnswersPresentation,
    SinglePlayerQuiz,
    SinglePlayerAnswersOverview
}

interface Props {}

export const SlideshowQuiz = (props: Props) => {
    const { quiz } = useQuizContext();
    const event = useEventContext();
    const params = useParams();
    const navigate = useNavigate();
    const quizId = parseInt(params.quizId!);
    const [currentScene, setCurrentScene] = useState(Scenes.Title);
    const [correctAnswers, setCorrectAnswers] = useState<Question[]>([]);
    const [incorrectAnswers, setIncorrectAnswers] = useState<Question[]>([]);

    const onStartClick = () => {
        setCurrentScene(Scenes.PresentationQuiz);
    };

    const onSinglePlayerStartClick = () => {
        setCurrentScene(Scenes.SinglePlayerQuiz);
    };

    const onFinishQuiz = () => {
        setCurrentScene(Scenes.QuizEndScreen);
    };

    const onShowAnswers = () => {
        setCurrentScene(Scenes.AnswersPresentation);
    };

    const onFinishSinglePlayerQuiz = () => {
        setCurrentScene(Scenes.SinglePlayerAnswersOverview);
    };

    const onEndQuiz = () => {
        navigate(NavUtils.getQuizSelectorPath(event?.alias));
    };

    const onQuestionAnswered = (isAnswerCorrect: boolean, quesiton: Question) => {
        if (isAnswerCorrect) {
            setCorrectAnswers([...correctAnswers, quesiton]);
            return;
        }
        setIncorrectAnswers([...incorrectAnswers, quesiton]);
    };

    const slideCustomFontStyles: CSSProperties = {
        ...quiz.styles?.fontStyles
    };

    const slideCustomBackgroundStyles: CSSProperties = {
        backgroundImage: quiz.styles?.backgroundStyles?.backgroundImageUrl
            ? `url("${quiz.styles?.backgroundStyles?.backgroundImageUrl}")`
            : undefined,
        ...omit(quiz.styles?.backgroundStyles, 'backgroundImageUrl')
    };

    const customTitlePageStyles: CSSProperties = {
        backgroundImage: quiz.styles?.titlePageStyles?.backgroundImageUrl
            ? `url("${quiz.styles?.titlePageStyles?.backgroundImageUrl}")`
            : undefined,
        ...omit(quiz.styles?.titlePageStyles, 'title', 'backgroundImageUrl')
    };
    const customTitleStyles: CSSProperties = {
        ...quiz.styles?.titlePageStyles?.title
    };

    const {
        // loading,
        // error,
        data: quizQuestions
        // refetch
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quizId }
    });

    const scenes = {
        [Scenes.Title]: () => (
            <StartScreen
                onStartClick={onStartClick}
                onSinglePlayerStartClick={onSinglePlayerStartClick}
                backgroundStyles={customTitlePageStyles}
                fontStyles={slideCustomFontStyles}
                titleStyles={customTitleStyles}
            />
        ),
        [Scenes.PresentationQuiz]: () => (
            <QuestionSlides
                quizQuestions={quizQuestions!}
                eventLogoUrl={event?.logo_url}
                finishQuiz={onFinishQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
                hoverMenu={<HoverMenu onCloseClick={onEndQuiz} />}
            />
        ),
        [Scenes.QuizEndScreen]: () => (
            <QuizEndScreen
                onShowAnswersClick={onShowAnswers}
                onEndQuiz={onEndQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
                titleStyles={customTitleStyles}
            />
        ),
        [Scenes.AnswersPresentation]: () => (
            <AnswerSlides
                quizQuestions={quizQuestions!}
                eventLogoUrl={event?.logo_url}
                finishQuiz={onFinishQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
                hoverMenu={<HoverMenu onCloseClick={onEndQuiz} />}
            />
        ),
        [Scenes.SinglePlayerQuiz]: () => (
            <AnswerSlides
                quizQuestions={quizQuestions!}
                eventLogoUrl={event?.logo_url}
                finishQuiz={onFinishSinglePlayerQuiz}
                backgroundStyles={slideCustomBackgroundStyles}
                fontStyles={slideCustomFontStyles}
                onAnswer={onQuestionAnswered}
                hoverMenu={<HoverMenu onCloseClick={onEndQuiz} />}
            />
        ),
        [Scenes.SinglePlayerAnswersOverview]: () => (
            <SinglePlayerResultScreen correctlyAnsweredQuestions={correctAnswers} incorrectlyAnsweredQuestions={incorrectAnswers} />
        )
    };

    return <div className="aq-content">{scenes[currentScene]()}</div>;
};
